import React, { useState, useEffect } from 'react';
import { completeActiveAndMLSTasks, revertActiveAndMLSTasks, applyDueDatesToTasks } from '../../services/firestoreService';

const DealFinancials = ({ selectedProject, updateProject, orgId, teamMembers = [], onTasksReloaded }) => {
  const [localValues, setLocalValues] = useState({
    listPrice: '',
    contractPrice: '',
    commissionPercent: '',
    brokerSplitType: 'percent',
    brokerSplitValue: '',
    transactionFee: '',
    tcFee: '',
    ucDate: '',
    listingDate: '',
    closingDate: '',
    primaryAgent: '',
  });
  const [saved, setSaved] = useState(false);
  const [showVariablePopup, setShowVariablePopup] = useState(false);
  const [variableDays, setVariableDays] = useState({ emdDays: '', inspectionDays: '' });
  const [skippedVariableDays, setSkippedVariableDays] = useState(false);

  useEffect(() => {
    if (selectedProject?.ucDate && !selectedProject?.variableDaysSet) {
      setAgentFields({
        opposingAgentName: selectedProject?.opposingAgentName || '',
        opposingAgentPhone: selectedProject?.opposingAgentPhone || '',
        opposingAgentEmail: selectedProject?.opposingAgentEmail || '',
      });
      setShowAgentPopup(true);
      setShowVariablePopup(true);
      completeActiveAndMLSTasks(orgId, selectedProject.id).catch(err =>
        console.error('[DealFinancials] Failed to auto-complete tasks on load:', err)
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedProject?.ucDate]);

  
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [agentFields, setAgentFields] = useState({ opposingAgentName: '', opposingAgentPhone: '', opposingAgentEmail: '' });

  useEffect(() => {
    if (selectedProject) {
      setLocalValues({
        listPrice: selectedProject.listPrice || '',
        contractPrice: selectedProject.contractPrice || '',
        commissionPercent: selectedProject.commissionPercent || '',
        brokerSplitType: selectedProject.brokerSplitType || 'percent',
        brokerSplitValue: selectedProject.brokerSplitValue || '',
        transactionFee: selectedProject.transactionFee || '',
        tcFee: selectedProject.tcFee || '',
        ucDate: selectedProject.ucDate || '',
        listingDate: selectedProject.listingDate || '',
        closingDate: selectedProject.closingDate || '',
        primaryAgent: selectedProject.primaryAgent || '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedProject?.ucDate, selectedProject?.listingDate, selectedProject?.closingDate, selectedProject?.listPrice, selectedProject?.contractPrice, selectedProject?.commissionPercent, selectedProject?.primaryAgent]);

  const handleChange = (field, value) => {
    setLocalValues(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    const prevUCDate = selectedProject?.ucDate || '';
    const newUCDate = localValues.ucDate;
    await updateProject(selectedProject.id, { ...localValues });
    if (newUCDate && !prevUCDate) {
      try {
        await completeActiveAndMLSTasks(orgId, selectedProject.id);
      } catch (err) {
        console.error('[DealFinancials] Failed to auto-complete tasks:', err);
      }
      setAgentFields({
        opposingAgentName: selectedProject?.opposingAgentName || '',
        opposingAgentPhone: selectedProject?.opposingAgentPhone || '',
        opposingAgentEmail: selectedProject?.opposingAgentEmail || '',
      });
      setShowAgentPopup(true);
      setShowVariablePopup(true);
      return;
    } else if (!newUCDate && prevUCDate) {
      try {
        await revertActiveAndMLSTasks(orgId, selectedProject.id);
      } catch (err) {
        console.error('[DealFinancials] Failed to revert tasks:', err);
      }
    }
    try {
      const updatedProject = { ...selectedProject, ...localValues };
      await applyDueDatesToTasks(orgId, selectedProject.id, updatedProject);
    } catch (err) {
      console.error('[DealFinancials] Failed to apply due dates:', err);
    }
    if (onTasksReloaded) await onTasksReloaded(selectedProject.id);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleVariablePopupSave = async () => {
    const variableDaysMap = {
      'Confirm EMD was received by Title company': Number(variableDays.emdDays) || 0,
      'Confirm Inspection date / repair request': Number(variableDays.inspectionDays) || 0,
      'Confirm EMD submitted to Title company': Number(variableDays.emdDays) || 0,
      'Schedule Inspection through ShowingTime': Number(variableDays.inspectionDays) || 0,
      'Inspections - Review and create amendment to contract': Number(variableDays.inspectionDays) || 0,
    };
    try {
      const updatedProject = { ...selectedProject, ...localValues };
      await applyDueDatesToTasks(orgId, selectedProject.id, updatedProject, variableDaysMap);
    } catch (err) {
      console.error('[DealFinancials] Failed to apply variable due dates:', err);
    }
    await updateProject(selectedProject.id, { variableDaysSet: true });
    setShowVariablePopup(false);
    if (onTasksReloaded) await onTasksReloaded(selectedProject.id);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const formatCurrency = (val) => {
    if (!val && val !== 0) return '';
    return Number(val).toLocaleString('en-US');
  };

  const parseCurrency = (val) => {
    return val.replace(/,/g, '');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid #d0d4da',
    borderRadius: 8,
    backgroundColor: '#ffffff',
    color: '#071D39',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: '#516469',
    marginBottom: 6,
    display: 'block',
  };

  const fieldWrap = { marginBottom: 16 };

  const prefixWrap = { position: 'relative' };

  const prefix = {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#89A8B1',
    fontSize: 14,
    pointerEvents: 'none',
  };

  const suffix = {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#89A8B1',
    fontSize: 14,
    pointerEvents: 'none',
  };

  const sectionHeader = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#75BB2E',
    borderBottom: '1px solid #d0d4da',
    paddingBottom: 8,
    marginBottom: 16,
    marginTop: 8,
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39' }}>Deal Financials</div>
          <div style={{ fontSize: 13, color: '#89A8B1', marginTop: 2 }}>Commission & fee breakdown for this deal</div>
        </div>
        <div style={{ fontSize: 28 }}>💰</div>
      </div>

      {/* Pricing Section */}
      <div style={sectionHeader}>Pricing</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>List Price</label>
          <div style={prefixWrap}>
            <span style={prefix}>$</span>
            <input type="text"
              value={localValues.listPrice ? formatCurrency(localValues.listPrice) : ''}
              onChange={e => handleChange('listPrice', parseCurrency(e.target.value))}
              style={{ ...inputStyle, paddingLeft: 28 }} placeholder="0" />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Contract Price</label>
          <div style={prefixWrap}>
            <span style={prefix}>$</span>
            <input type="text"
              value={localValues.contractPrice ? formatCurrency(localValues.contractPrice) : ''}
              onChange={e => handleChange('contractPrice', parseCurrency(e.target.value))}
              style={{ ...inputStyle, paddingLeft: 28 }} placeholder="0" />
          </div>
        </div>
      </div>

      {/* Commission Section */}
      <div style={sectionHeader}>Commission & Splits</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Commission Rate</label>
          <div style={prefixWrap}>
            <input type="number" min="0" max="100" step="0.1"
              value={localValues.commissionPercent}
              onChange={e => handleChange('commissionPercent', Math.max(0, Math.min(100, e.target.value)))}
              style={{ ...inputStyle, paddingRight: 28 }} placeholder="0" />
            <span style={suffix}>%</span>
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Broker Split</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={localValues.brokerSplitType}
              onChange={e => handleChange('brokerSplitType', e.target.value)}
              style={{ ...inputStyle, width: 64, flexShrink: 0, paddingLeft: 8, paddingRight: 4 }}>
              <option value="percent">%</option>
              <option value="flat">$</option>
            </select>
            <div style={{ ...prefixWrap, flex: 1 }}>
              {localValues.brokerSplitType === 'flat' && <span style={prefix}>$</span>}
              <input type="number" min="0" step="0.1"
                value={localValues.brokerSplitValue}
                onChange={e => handleChange('brokerSplitValue', Math.max(0, e.target.value))}
                style={{ ...inputStyle, paddingLeft: localValues.brokerSplitType === 'flat' ? 28 : 12, paddingRight: localValues.brokerSplitType !== 'flat' ? 28 : 12 }}
                placeholder="0" />
              {localValues.brokerSplitType !== 'flat' && <span style={suffix}>%</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Fees Section */}
      <div style={sectionHeader}>Fees</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Transaction Fee</label>
          <div style={prefixWrap}>
            <span style={prefix}>$</span>
            <input type="text"
              value={localValues.transactionFee ? formatCurrency(localValues.transactionFee) : ''}
              onChange={e => handleChange('transactionFee', parseCurrency(e.target.value))}
              style={{ ...inputStyle, paddingLeft: 28 }} placeholder="0" />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>TC Fee</label>
          <div style={prefixWrap}>
            <span style={prefix}>$</span>
            <input type="text"
              value={localValues.tcFee ? formatCurrency(localValues.tcFee) : ''}
              onChange={e => handleChange('tcFee', parseCurrency(e.target.value))}
              style={{ ...inputStyle, paddingLeft: 28 }} placeholder="0" />
          </div>
        </div>
      </div>

      {/* Dates Section */}
      <div style={sectionHeader}>Key Dates</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Listing Date</label>
          <input type="date"
            value={localValues.listingDate}
            onChange={e => handleChange('listingDate', e.target.value)}
            style={inputStyle} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Under Contract Date</label>
          <input type="date"
            value={localValues.ucDate}
            onChange={e => handleChange('ucDate', e.target.value)}
            style={inputStyle} />
          {localValues.ucDate && (
            <p style={{ fontSize: 11, color: '#75BB2E', marginTop: 4, fontWeight: 600 }}>
              ✓ Active + MLS tasks will auto-complete on save
            </p>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Closing Date</label>
          <input type="date"
            value={localValues.closingDate}
            onChange={e => handleChange('closingDate', e.target.value)}
            style={inputStyle} />
        </div>
        <div style={{ ...fieldWrap, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Primary Agent</label>
          <select
            value={localValues.primaryAgent}
            onChange={e => handleChange('primaryAgent', e.target.value)}
            style={inputStyle}>
            <option value="">— Select agent —</option>
            {teamMembers.map(m => {
              const name = `${m.firstName || ''} ${m.lastName || ''}`.trim();
              return <option key={m.uid || name} value={name}>{name}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Opposing Agent Popup */}
      {showAgentPopup && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: 32, width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39', marginBottom: 6 }}>
              {selectedProject?.type === 'Seller' ? "Who is the Buyer's Agent? 🤝" : "Who is the Seller's Agent? 🤝"}
            </div>
            <div style={{ fontSize: 13, color: '#89A8B1', marginBottom: 24 }}>
              Add their contact info for easy reference — you can always update it later in the deal header.
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Agent Name</label>
              <input type="text" value={agentFields.opposingAgentName}
                onChange={e => setAgentFields(prev => ({ ...prev, opposingAgentName: e.target.value }))}
                style={inputStyle} placeholder="Jane Smith" />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Agent Phone</label>
              <input type="text" value={agentFields.opposingAgentPhone}
                onChange={e => setAgentFields(prev => ({ ...prev, opposingAgentPhone: e.target.value }))}
                style={inputStyle} placeholder="5551234567" />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Agent Email</label>
              <input type="email" value={agentFields.opposingAgentEmail}
                onChange={e => setAgentFields(prev => ({ ...prev, opposingAgentEmail: e.target.value }))}
                style={inputStyle} placeholder="agent@email.com" />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button
                onClick={() => setShowAgentPopup(false)}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid #89A8B1', color: '#516469', fontWeight: 600, fontSize: 14, cursor: 'pointer', backgroundColor: '#fff', fontFamily: 'inherit' }}>
                Skip for now
              </button>
              <button
                onClick={async () => {
                  await updateProject(selectedProject.id, agentFields);
                  setShowAgentPopup(false);
                }}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', backgroundColor: '#75BB2E', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variable Days Popup */}
      {showVariablePopup && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 32,
            width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#071D39', marginBottom: 6 }}>
              A couple quick questions 📋
            </div>
            <div style={{ fontSize: 13, color: '#89A8B1', marginBottom: 24 }}>
              These are specified in the contract — enter the number of days from the contract date.
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>EMD due to Title Company — how many days?</label>
              <input
                type="number" min="0"
                value={variableDays.emdDays}
                onChange={e => setVariableDays(prev => ({ ...prev, emdDays: e.target.value }))}
                style={inputStyle}
                placeholder="e.g. 3"
              />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Inspection period — how many days?</label>
              <input
                type="number" min="0"
                value={variableDays.inspectionDays}
                onChange={e => setVariableDays(prev => ({ ...prev, inspectionDays: e.target.value }))}
                style={inputStyle}
                placeholder="e.g. 10"
              />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button
                onClick={() => { setShowVariablePopup(false); setSkippedVariableDays(true); }}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, border: '2px solid #89A8B1',
                  color: '#516469', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  backgroundColor: '#fff', fontFamily: 'inherit'
                }}>
                Skip for now
              </button>
              <button
                onClick={handleVariablePopupSave}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                  backgroundColor: '#75BB2E', color: '#fff', fontWeight: 700,
                  fontSize: 14, cursor: 'pointer', fontFamily: 'inherit'
                }}>
                Save Due Dates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Due Dates Button */}
      {skippedVariableDays && (
        <div style={{ backgroundColor: '#f4f5f7', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <p style={{ fontSize: 13, color: '#516469', margin: 0 }}>
            💡 You can set your contract due dates anytime using the button below.
          </p>
          <button
            onClick={() => { setShowVariablePopup(true); setSkippedVariableDays(false); }}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              backgroundColor: '#071D39', color: '#fff', fontWeight: 700,
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap'
            }}>
            Set Due Dates
          </button>
        </div>
      )}
      {!skippedVariableDays && localValues.ucDate && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button
            onClick={() => setShowVariablePopup(true)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid #d0d4da',
              backgroundColor: '#fff', color: '#071D39', fontWeight: 600,
              fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
            }}>
            ✏️ Update Due Dates
          </button>
        </div>
      )}

      {/* Save Button */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 24, gap: 8 }}>
        <button
          onClick={handleSave}
          style={{
            padding: '12px 48px',
            backgroundColor: '#071D39',
            color: '#ffffff',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}>
          Save Financials
        </button>
        {saved && (
          <span style={{ fontSize: 13, fontWeight: 600, color: '#75BB2E' }}>✓ Saved!</span>
        )}
      </div>
    </div>
  );
};

export default DealFinancials;